import React, { FC, useEffect, useState } from 'react'
import {
	AccordionComponent,
	BtnComponent,
	DatePickerMaskComponent,
	FormRowComponent,
	FormRowElement,
	IconGroupComponent,
	Indent,
	InnInput,
	InputComponent,
	UISizes,
	UITypes,
} from 'LibNeo'
import { useSelector } from 'react-redux'
import { FormNames } from 'Constants/FormNames'
import { FieldNames } from 'Constants/FieldNames'
import {
	AuthPassportFieldsToValidate,
	AUTHPOSITION_ITEMS,
	defaultValue,
	bgLimitRequestSelector,
	getDateText,
	getIconConsent,
	getLockedText,
	getPassportDataText,
	isAuthPassport,
	BgFormRowElement,
	BgTextArea,
	useIsFieldLocked,
	useChangeLead,
	fieldsToReset,
	RequestType,
	BgAddresComponent,
	AuthPassportkodInput,
	BgSelectWithOther,
	ActiveLead,
	fieldsByFormBlockLimit,
	LimitRequestNav,
	getOrgTypeByInn,
	TYPE_ORGANIZATION,
	BgLimitRequestFull,
} from 'Pages/ibro'
import { ADDRESS_LABEL, TYPE_ORIGIN_ADDRESS } from 'Constants/Address.constants'
import { Validators, formsSelectors, VALIDATOR_ERROR_MESSAGE } from 'Modules'
import { WrappedFormUtilsExtended } from 'Helpers'
import moment from 'moment'
import { DATE_FORMAT_FOR_BACKEND } from 'Constants/Global'
import { Callback } from 'Src/types/utilTypes'
import { useEffectOnce } from 'Src/hooks'

export const LeaderInfo: FC = () => {
	const getForm = useSelector(formsSelectors.getGlobalForm)
	const form = getForm(FormNames.BROKER.LIMIT) as WrappedFormUtilsExtended
	const item = useSelector(
		bgLimitRequestSelector.selectCurrentLimitRequest
	) as Required<BgLimitRequestFull>
	const { locked_fields, required_fields } = item
	const [validatePassport, setValidatePassport] = useState(false)

	const citizenshipSelectItems = ['Российская Федерация']
	const checkIsRFCitizen =
		form.getFieldValue(FieldNames.AUTHPERSONCITIZENSHIP) === citizenshipSelectItems[0]

	const onChangeLead = (activeLead: ActiveLead) => {
		if (!activeLead?.id) return
		const prevValidateState = validatePassport
		setValidatePassport(false)
		form.resetFields([...fieldsToReset, ...fieldsByFormBlockLimit[LimitRequestNav.DOCUMENTS]])
		form.setFieldsValue(
			{
				[FieldNames.AUTHPASSPORTSER]: undefined,
				[FieldNames.AUTHADDRESS]: undefined,
				[FieldNames.AUTHPERSONDATE]: undefined,
				[FieldNames.AUTHPERSONPLACE]: undefined,
				[FieldNames.AUTHPASSPORT]: checkIsRFCitizen
					? defaultValue[FieldNames.AUTHPASSPORT]
					: undefined,
			},
			() => {
				setValidatePassport(prevValidateState)
			}
		)
		if (activeLead?.fio)
			form.setFieldsValue({
				[FieldNames.FIO]: activeLead.fio,
			})
	}

	const { openModalAgreement, disabledAll, changeLead, activeLead } = useChangeLead(
		item,
		bgLimitRequestSelector.selectCurrentLimitRequest as Callback,
		RequestType.LIMIT_REQUEST,
		onChangeLead
	)
	const isLocked = useIsFieldLocked(locked_fields)

	const isSameFioForIp =
		(item?.[FieldNames.NAME]?.toLowerCase()?.endsWith(item?.[FieldNames.FIO]?.toLowerCase()) &&
			getOrgTypeByInn(item?.[FieldNames.INN]) === TYPE_ORGANIZATION.IP) ||
		getOrgTypeByInn(item?.[FieldNames.INN]) === TYPE_ORGANIZATION.OOO

	const buttonsBlock =
		item[FieldNames.ACTIVE_LEAD_PRESENT] && isSameFioForIp ? (
			<BtnComponent
				type={UITypes.PASSIVE}
				size={UISizes.SMALL}
				title="Получить новое согласие"
				onClick={changeLead}
				disabled={disabledAll}
			/>
		) : (
			<BtnComponent
				type={UITypes.DANGER}
				size={UISizes.SMALL}
				title="Получить согласие"
				icon="person-outline"
				onClick={openModalAgreement}
				disabled={disabledAll}
			/>
		)

	useEffect(() => {
		if (
			checkIsRFCitizen &&
			form.getFieldValue(FieldNames.AUTHPASSPORT) !== citizenshipSelectItems[0]
		) {
			form.setFieldsValue({
				[FieldNames.AUTHPASSPORT]: defaultValue[FieldNames.AUTHPASSPORT],
			})
		}
	}, [checkIsRFCitizen])

	const authpassportVal = form.getFieldValue(FieldNames.AUTHPASSPORT)

	useEffect(() => {
		const authPassport = isAuthPassport(authpassportVal)
		if (authPassport) {
			form.validateFields(AuthPassportFieldsToValidate)
		}
		setValidatePassport(authPassport)
	}, [authpassportVal])

	useEffect(() => {
		if (form.isFieldTouched(FieldNames.AUTHPERSONDATE)) {
			const birthDate = form.getFieldValue(FieldNames.AUTHPERSONDATE)
			if (moment(birthDate).isValid() && !locked_fields.includes(FieldNames.AUTHPASSPORTDATEGIVE)) {
				form.validateFieldsAndScroll([FieldNames.AUTHPASSPORTDATEGIVE])
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [form.getFieldValue(FieldNames.AUTHPERSONDATE)])

	useEffectOnce(form.resetFields)

	return (
		<Indent top={40}>
			<AccordionComponent
				id="LeaderInfo"
				titleText="Общие сведения"
				noExpand
				buttonsBlock={buttonsBlock}
			>
				<Indent top={24}>
					<FormRowComponent isString label="ФИО">
						<FormRowElement size={UISizes.MEDIUM}>
							{item[FieldNames.FIO]}
							<IconGroupComponent {...getIconConsent(activeLead?.bearer)} />
							<InputComponent
								fieldName={FieldNames.FIO}
								form={form}
								fieldOptions={{
									rules: [
										{
											validator: Validators.checkIsEqualText({
												required: true,
												payload: {
													text: activeLead?.[FieldNames.FIO] || '',
												},
											}),
										},
										{
											validator: Validators.checkIsSameFioForIp({
												payload: {
													text: item?.[FieldNames.NAME],
													orgType: getOrgTypeByInn(item?.[FieldNames.INN]) as TYPE_ORGANIZATION,
												},
											}),
										},
									],
									initialValue: item[FieldNames.FIO],
								}}
								type="hidden"
							/>
						</FormRowElement>
					</FormRowComponent>

					<BgFormRowElement
						fields={required_fields}
						fieldName={FieldNames.AUTHPERSONCITIZENSHIP}
						lockedFields={locked_fields}
						label="Гражданство"
						lockedElem={getLockedText(item.authpersoncitizenship)}
					>
						<BgSelectWithOther
							items={citizenshipSelectItems}
							form={form}
							lockedFields={locked_fields}
							fieldOptions={{
								rules: [
									{
										validator: Validators.checkCyrillicsWithHyphen({
											required: required_fields.includes(FieldNames.AUTHPERSONCITIZENSHIP),
										}),
									},
								],
								initialValue: item[FieldNames.AUTHPERSONCITIZENSHIP] || citizenshipSelectItems[0],
							}}
							fieldName={FieldNames.AUTHPERSONCITIZENSHIP}
						/>
					</BgFormRowElement>

					<BgFormRowElement
						fields={required_fields}
						fieldName={FieldNames.AUTHPERSONDATE}
						lockedFields={locked_fields}
						label="Дата рождения"
						lockedElem={getDateText(item.authpersondate)}
						size={UISizes.SMALL}
					>
						<DatePickerMaskComponent
							field={FieldNames.AUTHPERSONDATE}
							form={form}
							initialValue={item[FieldNames.AUTHPERSONDATE]}
							fieldOptions={{
								rules: [
									{
										validator: Validators.checkMinMaxAge({
											required: required_fields.includes(FieldNames.AUTHPERSONDATE),
											messageError: {
												[VALIDATOR_ERROR_MESSAGE.LESS]: 'authpersondate.bg_age_less_than_18',
												[VALIDATOR_ERROR_MESSAGE.MORE]: 'authpersondate.bg_age_more_than_100',
											},
										}),
									},
								],
							}}
						/>
					</BgFormRowElement>

					<BgFormRowElement
						fields={required_fields}
						fieldName={FieldNames.AUTHPERSONPLACE}
						lockedFields={locked_fields}
						label="Место рождения"
						lockedElem={getLockedText(item[FieldNames.AUTHPERSONPLACE])}
					>
						<BgTextArea
							form={form}
							fieldName={FieldNames.AUTHPERSONPLACE}
							item={item}
							initialValue={item[FieldNames.AUTHPERSONPLACE]}
						/>
					</BgFormRowElement>

					<BgAddresComponent
						form={form}
						defaultAddress={item[FieldNames.AUTHADDRESS]}
						fieldName={FieldNames.AUTHADDRESS}
						label={ADDRESS_LABEL.AUTH_ADDRESS}
						origin={TYPE_ORIGIN_ADDRESS.BG_REQUEST_ADDRESS}
						lockedElem={item[FieldNames.AUTHADDRESS]?.result}
						fields={required_fields}
						lockedFields={locked_fields}
						fieldOptions={{
							rules: [
								{
									validator: Validators.checkRequired({
										required: required_fields?.includes(FieldNames.AUTHADDRESS),
									}),
								},
							],
						}}
					/>

					<BgFormRowElement
						fields={required_fields}
						fieldName={FieldNames.AUTHINN}
						lockedFields={locked_fields}
						label="ИНН"
						lockedElem={getLockedText(item.authinn)}
					>
						<InnInput
							label=""
							fieldName={FieldNames.AUTHINN}
							form={form}
							initialValue={item[FieldNames.AUTHINN] || undefined}
							isTwelve
							required={required_fields.includes(FieldNames.AUTHINN)}
						/>
					</BgFormRowElement>
				</Indent>
			</AccordionComponent>
			<Indent top={48}>
				<AccordionComponent id="LeaderDoc" titleText="Документ, удостоверяющий личность" noExpand>
					<Indent top={24}>
						<BgFormRowElement
							fields={required_fields}
							fieldName={FieldNames.AUTHPASSPORT}
							lockedFields={locked_fields}
							label="Вид документа"
							lockedElem={item.authpassport}
						>
							<InputComponent
								fieldName={FieldNames.AUTHPASSPORT}
								form={form}
								fieldOptions={{
									initialValue:
										item[FieldNames.AUTHPASSPORT] ||
										(checkIsRFCitizen ? defaultValue[FieldNames.AUTHPASSPORT] : undefined),
								}}
							/>
						</BgFormRowElement>

						{(required_fields.includes(FieldNames.AUTHPASSPORTSER) ||
							required_fields.includes(FieldNames.AUTHPASSPORTNUM)) && (
							<FormRowComponent
								isString={
									isLocked(FieldNames.AUTHPASSPORTSER) && isLocked(FieldNames.AUTHPASSPORTNUM)
								}
								label="Серия и номер"
							>
								<>
									{required_fields.includes(FieldNames.AUTHPASSPORTSER) && (
										<FormRowElement size={UISizes.SMALL} fixWidth>
											<Indent right={12}>
												{isLocked(FieldNames.AUTHPASSPORTSER) ? (
													getPassportDataText(item.authpassportser, 'ser')
												) : (
													<InputComponent
														form={form}
														fieldName={FieldNames.AUTHPASSPORTSER}
														fieldOptions={{
															rules: [
																{
																	validator: Validators.checkRequired({
																		required: required_fields.includes(FieldNames.AUTHPASSPORTSER),
																	}),
																},
															],
															initialValue: item[FieldNames.AUTHPASSPORTSER] || undefined,
														}}
														{...(validatePassport && {
															format: '## ##',
															mask: '_',
															placeholder: '__ __',
														})}
													/>
												)}
											</Indent>
										</FormRowElement>
									)}
									{required_fields.includes(FieldNames.AUTHPASSPORTNUM) && (
										<FormRowElement size={UISizes.SMALL} fixWidth>
											{isLocked(FieldNames.AUTHPASSPORTNUM) ? (
												getPassportDataText(item.authpassportnum, 'num')
											) : (
												<InputComponent
													form={form}
													fieldName={FieldNames.AUTHPASSPORTNUM}
													fieldOptions={{
														rules: [
															{
																validator: Validators.checkRequired({
																	required: required_fields.includes(FieldNames.AUTHPASSPORTNUM),
																}),
															},
														],
														initialValue: item[FieldNames.AUTHPASSPORTNUM] || undefined,
													}}
													{...(validatePassport && {
														format: '######',
														mask: '_',
														placeholder: '______',
													})}
												/>
											)}
										</FormRowElement>
									)}
								</>
							</FormRowComponent>
						)}

						<BgFormRowElement
							fields={required_fields}
							fieldName={FieldNames.AUTHPASSPORTDATEGIVE}
							lockedFields={locked_fields}
							label="Дата выдачи"
							lockedElem={getDateText(item.authpassportdategive)}
							size={UISizes.SMALL}
						>
							<DatePickerMaskComponent
								field={FieldNames.AUTHPASSPORTDATEGIVE}
								form={form}
								initialValue={item[FieldNames.AUTHPASSPORTDATEGIVE]}
								fieldOptions={{
									rules: [
										{
											validator: form.getFieldValue(FieldNames.AUTHPERSONDATE)
												? Validators.check14yearsPassportInterval({
														required: required_fields.includes(FieldNames.AUTHPASSPORTDATEGIVE),
														messageError: 'authpassportdategive.bg_must_be_14_years_interval',
														payload: {
															birthDate: form.getFieldValue(FieldNames.AUTHPERSONDATE),
														},
												  })
												: Validators.checkRequired({
														required: required_fields.includes(FieldNames.AUTHPASSPORTDATEGIVE),
												  }),
										},
										{
											validator: Validators.checkMoreDate({
												required: required_fields.includes(FieldNames.AUTHPASSPORTDATEGIVE),
												payload: {
													min: moment(new Date()).format(DATE_FORMAT_FOR_BACKEND),
												},
												messageError: 'broker.more_end_date',
											}),
										},
									],
								}}
							/>
						</BgFormRowElement>

						<AuthPassportkodInput
							form={form}
							item={item}
							validatePassport={validatePassport}
							initialValue={item[FieldNames.AUTHPASSPORTKOD]}
						/>

						<BgFormRowElement
							fields={required_fields}
							fieldName={FieldNames.AUTHPASSPORTWHOGIVE}
							lockedFields={locked_fields}
							label="Кем выдан"
							lockedElem={getLockedText(item.authpassportwhogive)}
						>
							<BgTextArea
								form={form}
								fieldName={FieldNames.AUTHPASSPORTWHOGIVE}
								item={item}
								initialValue={item[FieldNames.AUTHPASSPORTWHOGIVE]}
							/>
						</BgFormRowElement>
					</Indent>
				</AccordionComponent>
			</Indent>

			<BgFormRowElement
				fields={required_fields}
				fieldName={FieldNames.AUTHPOSITION}
				lockedFields={locked_fields}
				label="Должность"
				lockedElem={getLockedText(item.authposition)}
			>
				<BgSelectWithOther
					form={form}
					items={AUTHPOSITION_ITEMS}
					fieldName={FieldNames.AUTHPOSITION}
					lockedFields={locked_fields}
					fieldOptions={{
						rules: [
							{
								validator: Validators.checkRequired({
									required: required_fields.includes(FieldNames.AUTHPOSITION),
								}),
							},
						],
						initialValue: item[FieldNames.AUTHPOSITION] || undefined,
					}}
				/>
			</BgFormRowElement>

			<BgFormRowElement
				fields={required_fields}
				fieldName={FieldNames.ACTINGUNDER}
				lockedFields={locked_fields}
				label="Действует на основании"
				lockedElem={getLockedText(item.actingunder)}
			>
				<InputComponent
					form={form}
					fieldName={FieldNames.ACTINGUNDER}
					fieldOptions={{
						rules: [
							{
								validator: Validators.checkRequired({
									required: required_fields.includes(FieldNames.ACTINGUNDER),
								}),
							},
						],
						initialValue: item[FieldNames.ACTINGUNDER] || defaultValue[FieldNames.ACTINGUNDER],
					}}
				/>
			</BgFormRowElement>
		</Indent>
	)
}
